class CartUpsellModal {
  constructor() {
    this.modal = document.getElementById('CartUpsellModal');
    if (!this.modal) return;

    this.overlay = this.modal.querySelector('[data-upsell-overlay]');
    this.closeBtn = this.modal.querySelector('[data-upsell-close]');

    this.config = this.readConfig();
    this.drawerObserver = null;
    this.wasDrawerActive = false;

    this.bindEvents();
  }

  readConfig() {
    const defaults = {
      desktop: { enabled: true, trigger: 'drawer_open', frequency: 'session', waitMs: 0 },
      mobile:  { enabled: true, trigger: 'checkout_click', frequency: 'session', waitMs: 0 },
    };
    const node = this.modal.querySelector('script[data-upsell-config]');
    if (!node) return defaults;
    try {
      const parsed = JSON.parse(node.textContent);
      return {
        desktop: { ...defaults.desktop, ...(parsed.desktop || {}) },
        mobile:  { ...defaults.mobile,  ...(parsed.mobile  || {}) },
      };
    } catch {
      return defaults;
    }
  }

  // Resolve active platform config based on viewport
  currentPlatform() {
    return window.innerWidth >= 1024 ? 'desktop' : 'mobile';
  }

  currentConfig() {
    return this.config[this.currentPlatform()];
  }

  storageKeys() {
    const platform = this.currentPlatform();
    return {
      session: `biribox_upsell_shown_${platform}`,
      timer:   `biribox_upsell_last_closed_${platform}`,
    };
  }

  shouldShow() {
    const cfg = this.currentConfig();
    if (!cfg.enabled) return false;

    const keys = this.storageKeys();

    if (cfg.frequency === 'always') return true;

    if (cfg.frequency === 'session') {
      try {
        return !sessionStorage.getItem(keys.session);
      } catch {
        return !this._shownSession;
      }
    }

    if (cfg.frequency === 'timer') {
      try {
        const last = parseInt(localStorage.getItem(keys.timer), 10);
        if (!last) return true;
        return (Date.now() - last) >= (cfg.waitMs || 0);
      } catch {
        return true;
      }
    }

    return true;
  }

  bindEvents() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Checkout click trigger: intercept form submit. Only blocks when
    // the active platform is set to checkout_click; otherwise submit proceeds.
    cartDrawer.addEventListener('submit', (e) => {
      if (e.target.id !== 'CartDrawer-Form') return;
      const cfg = this.currentConfig();
      if (!cfg.enabled || cfg.trigger !== 'checkout_click') return;
      if (!this.shouldShow()) return;
      e.preventDefault();
      this.open();
    });

    // Drawer-open trigger: observe the `active` class toggle on <cart-drawer>
    this.drawerObserver = new MutationObserver(() => {
      const isActive = cartDrawer.classList.contains('active');
      if (isActive && !this.wasDrawerActive) {
        const cfg = this.currentConfig();
        if (cfg.enabled && cfg.trigger === 'drawer_open' && this.shouldShow()) {
          this.open();
        }
      }
      this.wasDrawerActive = isActive;
    });
    this.drawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });

    // Close the upsell when the drawer closes (click on close button or overlay)
    // Uses event delegation so it survives cart-drawer renderContents() re-renders
    cartDrawer.addEventListener('click', (e) => {
      if (e.target.closest('.close-cart-drawer') || e.target.closest('#CartDrawer-Overlay')) {
        this.close();
      }
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    this.modal.addEventListener('keyup', (e) => {
      if (e.code === 'Escape') this.close();
    });

    // Re-sync position when other modals open/close (scrollbar changes viewport width)
    document.addEventListener('modalClosed', () => {
      if (!this.modal.hasAttribute('open')) return;
      requestAnimationFrame(() => this.syncHeightWithDrawer());
    });

    window.addEventListener('resize', () => {
      if (this.modal.hasAttribute('open')) this.syncHeightWithDrawer();
    });
  }

  open() {
    const cfg = this.currentConfig();

    // Mark as shown for `session` mode on open (so it doesn't reappear mid-session
    // even if the user never closes it)
    if (cfg.frequency === 'session') {
      try {
        sessionStorage.setItem(this.storageKeys().session, '1');
      } catch {
        this._shownSession = true;
      }
    }

    this.modal.setAttribute('open', '');
    this.modal.setAttribute('aria-hidden', 'false');
    this.syncHeightWithDrawer();
  }

  syncHeightWithDrawer() {
    if (window.innerWidth < 1024) return;
    const drawer = document.getElementById('CartDrawer');
    const body = this.modal.querySelector('.cart-upsell-modal__body');
    if (!drawer || !body) return;
    const rect = drawer.getBoundingClientRect();
    body.style.top = rect.top + 'px';
    body.style.height = rect.height + 'px';
    const modalWidth = body.offsetWidth || 400;
    body.style.left = (rect.left - modalWidth - 8) + 'px';
    body.style.right = 'auto';
  }

  close() {
    const wasOpen = this.modal.hasAttribute('open');
    this.modal.removeAttribute('open');
    this.modal.setAttribute('aria-hidden', 'true');

    // Timer mode stamps on close (wait period starts counting now)
    if (wasOpen && this.currentConfig().frequency === 'timer') {
      try {
        localStorage.setItem(this.storageKeys().timer, String(Date.now()));
      } catch {
        // localStorage unavailable — timer mode effectively behaves like 'always'
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartUpsellModal());
} else {
  new CartUpsellModal();
}
